/**
 * Section Navigation Tracker Service
 * Tracks how users navigate to section pages for Sentry analytics
 *
 * Navigation sources:
 * - all_link: Section opened from the Home page section "All" link in the header
 * - footer_navigation: Section opened via footer navigation tabs
 * - widget: Section opened from Game client widget (lobby opened in ingame view with openSection param)
 * - banner: Section opened via openSection query param from an external source (non-ingame)
 *
 * Tracks:
 * - egt.lobby.section_navigation.last_section: The last section navigated to
 * - egt.lobby.section_navigation.last_source: The navigation source used to reach the last section
 * - egt.lobby.section_navigation.all_link_count: Number of times sections were opened via "All" link
 * - egt.lobby.section_navigation.footer_navigation_count: Number of times sections were opened via footer navigation
 * - egt.lobby.section_navigation.widget_count: Number of times sections were opened via Game client widget (ingame)
 * - egt.lobby.section_navigation.banner_count: Number of times sections were opened via banner (openSection param, non-ingame)
 * - egt.lobby.section_navigation.history: Comma-separated history of navigations (source:section)
 */

export type SectionNavigationSource = 'all_link' | 'footer_navigation' | 'widget' | 'banner';

interface SectionNavigationEntry {
    section: string;
    source: SectionNavigationSource;
}

class SectionNavigationTracker {
    private static instance: SectionNavigationTracker;
    private lastNavigation: SectionNavigationEntry;
    private sourceCounts: Record<SectionNavigationSource, number>;
    private history: string[] = [];
    private maxHistorySize: number = 50;

    private constructor() {
        this.lastNavigation = {
            section: '',
            source: 'all_link',
        };
        this.sourceCounts = {
            all_link: 0,
            footer_navigation: 0,
            widget: 0,
            banner: 0,
        };
    }

    public static getInstance(): SectionNavigationTracker {
        if (!SectionNavigationTracker.instance) {
            SectionNavigationTracker.instance = new SectionNavigationTracker();
        }
        return SectionNavigationTracker.instance;
    }

    /**
     * Track a section navigation event
     * @param sectionName - The section that was navigated to
     * @param source - How the section was reached
     */
    public trackSectionNavigation(sectionName: string, source: SectionNavigationSource): void {
        this.lastNavigation = {
            section: sectionName,
            source,
        };

        this.sourceCounts[source]++;

        // Add to history
        this.history.push(`${source}:${sectionName}`);
        if (this.history.length > this.maxHistorySize) {
            this.history.shift();
        }

        if (process.env['NODE_ENV'] === 'development') {
            console.log(`[SectionNavigationTracker] ${sectionName} via ${source}`);
        }
    }

    /**
     * Get section navigation data formatted for Sentry
     */
    public getFormattedData(): Record<string, string | number> {
        const formatted: Record<string, string | number> = {
            'egt.lobby.section_navigation.last_section': this.lastNavigation.section,
            'egt.lobby.section_navigation.last_source': this.lastNavigation.source,
            'egt.lobby.section_navigation.all_link_count': this.sourceCounts.all_link,
            'egt.lobby.section_navigation.footer_navigation_count': this.sourceCounts.footer_navigation,
            'egt.lobby.section_navigation.widget_count': this.sourceCounts.widget,
            'egt.lobby.section_navigation.banner_count': this.sourceCounts.banner,
        };

        if (this.history.length > 0) {
            formatted['egt.lobby.section_navigation.history'] = this.history.join(',');
        }

        return formatted;
    }

    /**
     * Get the current navigation data (for debugging)
     */
    public getNavigationData(): {
        lastNavigation: SectionNavigationEntry;
        sourceCounts: Record<SectionNavigationSource, number>;
        history: string[];
    } {
        return {
            lastNavigation: { ...this.lastNavigation },
            sourceCounts: { ...this.sourceCounts },
            history: [...this.history],
        };
    }

    /**
     * Clear navigation data (useful for new sessions)
     */
    public clear(): void {
        this.lastNavigation = {
            section: '',
            source: 'all_link',
        };
        this.sourceCounts = {
            all_link: 0,
            footer_navigation: 0,
            widget: 0,
            banner: 0,
        };
        this.history = [];
    }
}

// Export singleton instance
export const sectionNavigationTracker = SectionNavigationTracker.getInstance();
